import React, { useEffect, useState } from 'react';
import { CalendarIcon, MapPinIcon, ArrowRightIcon, ChevronDownIcon, MicIcon, UsersIcon, UtensilsIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface HomePageProps {
  setActiveTab: (tab: string) => void;
}
export function HomePage({
  setActiveTab
}: HomePageProps) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    mins: 0,
    secs: 0
  });
  useEffect(() => {
    const targetDate = new Date('2026-04-25T09:30:00').getTime();
    const updateCountdown = () => {
      const now = new Date().getTime();
      const difference = targetDate - now;
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor(difference % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
          mins: Math.floor(difference % (1000 * 60 * 60) / (1000 * 60)),
          secs: Math.floor(difference % (1000 * 60) / 1000)
        });
      }
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000); // Update every second
    return () => clearInterval(interval);
  }, []);
  const AnimatedNumber = ({
    value,
    label
  }: {
    value: number;
    label: string;
  }) => <div className="text-center flex-1 relative overflow-hidden" data-id="element-21">
      <AnimatePresence mode="popLayout" data-id="element-22">
        <motion.div key={value} initial={{
        y: 20,
        opacity: 0,
        scale: 0.8
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: -20,
        opacity: 0,
        scale: 0.8
      }} transition={{
        duration: 0.3,
        type: 'spring',
        stiffness: 300,
        damping: 25
      }} className="text-3xl font-black text-[#7B2D8E] tabular-nums" data-id="element-23">
          {value.toString().padStart(2, '0')}
        </motion.div>
      </AnimatePresence>
      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1" data-id="element-24">
        {label}
      </div>
    </div>;
  return <div className="pb-12" data-id="element-25">
      {/* Hero Section */}
      <div className="relative h-[480px] w-full overflow-hidden" data-id="element-26">
        <motion.div initial={{
        scale: 1.1
      }} animate={{
        scale: 1
      }} transition={{
        duration: 1.5,
        ease: 'easeOut'
      }} className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/8xDMYnLBAKWxqRU8uDHqaj/Screenshot_2025-12-30_112151.png")'
      }} data-id="element-27" />
        {/* Radial gradient for depth */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.4)_100%)]" data-id="element-28" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#374151] via-[#7B2D8E]/80 to-transparent mix-blend-multiply" data-id="element-29" />
        <div className="absolute inset-0 bg-black/20" data-id="element-30" />

        <div className="absolute bottom-0 left-0 w-full p-6 text-white z-10" data-id="element-31">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} data-id="element-32">
            <span className="inline-block px-4 py-1.5 mb-4 text-xs font-bold tracking-widest text-[#7B2D8E] uppercase bg-gradient-to-r from-[#D4A017] to-[#F3C642] rounded-full shadow-[0_4px_12px_rgba(212,160,23,0.4)]" data-id="element-33">
              3rd Annual
            </span>
            <h1 className="text-4xl font-black leading-tight tracking-tight text-[#D4A017] drop-shadow-lg mb-2" data-id="element-34">
              2026 Men Getting Real
            </h1>
            <h2 className="text-2xl font-bold text-white mb-6 drop-shadow-md opacity-90" data-id="element-35">
              Conference
            </h2>

            <div className="flex flex-col space-y-3 text-sm font-medium text-gray-100 bg-black/20 backdrop-blur-sm p-4 rounded-2xl border border-white/10" data-id="element-36">
              <div className="flex items-center" data-id="element-37">
                <CalendarIcon className="w-5 h-5 mr-3 text-[#D4A017]" data-id="element-38" />
                April 25, 2026
              </div>
              <div className="flex items-center" data-id="element-39">
                <MapPinIcon className="w-5 h-5 mr-3 text-[#D4A017]" data-id="element-40" />
                Spirit of Excellence Church
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div animate={{
        y: [0, 8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} className="absolute bottom-4 right-6 text-white/70 flex flex-col items-center" data-id="element-41">
          <span className="text-[10px] font-bold uppercase tracking-widest mb-1" data-id="element-42">
            Scroll
          </span>
          <ChevronDownIcon className="w-4 h-4" data-id="element-43" />
        </motion.div>
      </div>

      {/* Content Section */}
      <div className="px-6 py-8 -mt-6 relative bg-gray-50 rounded-t-[2rem] shadow-[0_-8px_30px_rgba(0,0,0,0.1)] z-20" data-id="element-44">
        {/* Countdown */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="flex justify-between p-5 mb-10 bg-white rounded-2xl shadow-lg shadow-purple-900/5 border border-purple-100" data-id="element-45">
          <AnimatedNumber value={timeLeft.days} label="Days" data-id="element-46" />
          <div className="w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent mx-2" data-id="element-47" />
          <AnimatedNumber value={timeLeft.hours} label="Hours" data-id="element-48" />
          <div className="w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent mx-2" data-id="element-49" />
          <AnimatedNumber value={timeLeft.mins} label="Mins" data-id="element-50" />
          <div className="w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent mx-2" data-id="element-51" />
          <AnimatedNumber value={timeLeft.secs} label="Secs" data-id="element-52" />
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="mb-10 text-center px-2" data-id="element-53">
          <h3 className="text-2xl font-black text-[#374151] mb-4 leading-tight" data-id="element-54">
            "What If This Conference Changed Everything?"
          </h3>

          <div className="flex items-center justify-center gap-3 mb-4" data-id="element-55">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#D4A017]" data-id="element-56" />
            <div className="w-2 h-2 rounded-full bg-[#D4A017]" data-id="element-57" />
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#D4A017]" data-id="element-58" />
          </div>

          <p className="text-gray-600 leading-relaxed text-lg font-medium" data-id="element-59">
            Come ready to go deeper, grow sharper, and lead with confidence.
            Join us for our 3rd Annual Men Getting Real Conference.
          </p>
        </motion.div>

        {/* Quick Actions */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6
      }} className="space-y-4 mb-12" data-id="element-60">
          <a href="https://share.deftform.com/bJaXH8" target="_blank" rel="noopener noreferrer" className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-[#7B2D8E] to-[#5a2168] text-white rounded-2xl font-bold shadow-xl shadow-purple-900/20 hover:shadow-purple-900/40 transition-all active:scale-[0.98] group" data-id="element-61">
            <span className="text-lg" data-id="element-62">Register Now</span>
            <div className="bg-white/20 p-2 rounded-xl group-hover:bg-white/30 transition-colors" data-id="element-63">
              <ArrowRightIcon className="w-5 h-5 text-[#D4A017]" data-id="element-64" />
            </div>
          </a>

          <button onClick={() => setActiveTab('schedule')} className="w-full flex items-center justify-between p-4 bg-white border-2 border-gray-100 text-[#374151] rounded-2xl font-bold shadow-sm hover:border-[#D4A017]/50 hover:bg-gray-50 transition-all active:scale-[0.98] group" data-id="element-65">
            <span className="text-lg" data-id="element-66">View Event Schedule</span>
            <div className="bg-gray-100 p-2 rounded-xl group-hover:bg-[#D4A017]/10 transition-colors" data-id="element-67">
              <ArrowRightIcon className="w-5 h-5 text-[#7B2D8E]" data-id="element-68" />
            </div>
          </button>
        </motion.div>

        {/* Highlights Preview */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7
      }} data-id="element-69">
          <div className="flex items-center justify-between mb-4" data-id="element-70">
            <h4 className="font-bold text-gray-900 text-lg" data-id="element-71">
              Event Highlights
            </h4>
            <button onClick={() => setActiveTab('schedule')} className="text-sm font-bold text-[#7B2D8E]" data-id="element-72">
              See all
            </button>
          </div>

          <div className="space-y-3" data-id="element-73">
            {[{
            title: 'Keynote: "Walking in Your Calling"',
            icon: MicIcon,
            desc: 'The main event'
          }, {
            title: '3 Breakout Sessions',
            icon: UsersIcon,
            desc: 'Financial, Missionary, Marriage'
          }, {
            title: 'Fellowship Luncheon',
            icon: UtensilsIcon,
            desc: 'Break bread together'
          }].map((item, i) => <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex items-center relative overflow-hidden group" data-id="element-74">
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#D4A017] rounded-l-2xl opacity-80" data-id="element-75" />
                <div className="bg-purple-50 p-3 rounded-xl mr-4 text-[#7B2D8E]" data-id="element-76">
                  <item.icon className="w-5 h-5" data-id="element-77" />
                </div>
                <div data-id="element-78">
                  <h5 className="font-bold text-gray-900" data-id="element-79">{item.title}</h5>
                  <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-80">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>
    </div>;
}